import { MODAL_DIALOG_WIDTH } from 'modules/core/constants';
import React, { useEffect, useState } from 'react';
import { Modal, ModalProps } from 'react-bootstrap';
import { Alignment, ButtonVariant, ModalTheme } from 'modules/core/enums';
import { ConfirmationDialog } from 'components/Modals/ConfirmationDialog/ConfirmationDialog';

import { ModalButton, Content, ModalContainer, Title, Footer, ModalBackdrop } from './style';

interface IModalDialogProps extends ModalProps {
  children: React.ReactNode;
  title?: string;
  submitButtonTitle?: string;
  onSubmitModal?: VoidFunction;
  cancelButtonTitle?: string;
  modalSize?: string;
  minModalSize?: string;
  showFooter?: boolean;
  showHeader?: boolean;
  upperCaseTitle?: boolean;
  alignFooterContent?: Alignment;
  additionalButton?: React.ReactNode;
  verticallyCentered?: boolean;
  showCloseButton?: boolean;
  header?: React.ReactNode;
  scrollable?: boolean;
  modalTheme?: ModalTheme;
  modalStyle?: string;
  isDisabled?: boolean;
  backdrop?: boolean | 'static' | undefined;
  keyboard?: boolean;
  backdropClassName?: string;
  dialogClassName?: string;
  isDirty?: boolean;
  isAdjustDeal?: boolean;
  animation?: boolean;
  onExited?: () => void;
}

export const ModalDialog: React.FC<IModalDialogProps> = ({
  children,
  title,
  submitButtonTitle,
  show,
  onHide,
  isDisabled = false,
  cancelButtonTitle,
  onSubmitModal,
  modalSize = MODAL_DIALOG_WIDTH.MD,
  minModalSize = modalSize,
  showFooter = true,
  showHeader = true,
  upperCaseTitle = false,
  alignFooterContent = Alignment.RIGHT,
  additionalButton,
  verticallyCentered = false,
  header,
  showCloseButton = true,
  scrollable = false,
  modalTheme = ModalTheme.DEFAULT,
  modalStyle,
  backdrop = true,
  keyboard = true,
  backdropClassName = 'backdrop-full-screen',
  dialogClassName,
  isDirty = false,
  isAdjustDeal = false,
  container,
  animation = true,
  onExited,
}) => {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  useEffect(() => {
    if (!show || !onSubmitModal || isDisabled || showConfirmationDialog) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;

      const target = event.target as HTMLElement;
      const tag = target.tagName;
      const role = target.role;

      if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'SELECT' || role === 'button') return;

      event.preventDefault();
      onSubmitModal();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, onSubmitModal, isDisabled, showConfirmationDialog]);

  return (
    <>
      <ModalContainer
        show={show}
        animation={animation}
        onExited={onExited}
        onHide={() => {
          isDirty ? setShowConfirmationDialog(true) : onHide && onHide();
        }}
        backdrop={backdrop}
        keyboard={keyboard}
        centered={verticallyCentered}
        scrollable={scrollable}
        $modalStyle={modalStyle}
        backdropClassName={backdropClassName}
        dialogClassName={dialogClassName}
        $modalTheme={modalTheme}
        $modalSize={modalSize}
        $minModalSize={minModalSize}
        $isAdjustDeal={isAdjustDeal}
        container={container}
      >
        {showConfirmationDialog && <ModalBackdrop showBackdrop={showConfirmationDialog} />}
        {showHeader && (
          <Modal.Header>
            {header || <Title $uppercase={upperCaseTitle}>{title}</Title>}
            {showCloseButton && (
              <button type="button" className="btn-close" aria-label="Close" onClick={onHide}></button>
            )}
          </Modal.Header>
        )}
        <Content>{children}</Content>
        {showFooter && (
          <Footer $alignFooterContent={alignFooterContent}>
            {additionalButton && additionalButton}
            {cancelButtonTitle && (
              <ModalButton onClick={onHide} buttontheme={ButtonVariant.OUTLINE}>
                {cancelButtonTitle}
              </ModalButton>
            )}
            {submitButtonTitle && (
              <ModalButton onClick={onSubmitModal || onHide} buttontheme={ButtonVariant.SUBMIT} disabled={isDisabled}>
                {submitButtonTitle}
              </ModalButton>
            )}
          </Footer>
        )}
      </ModalContainer>
      {showConfirmationDialog && (
        <ConfirmationDialog
          show={showConfirmationDialog}
          onHide={() => {
            setShowConfirmationDialog(false);
          }}
          onSubmitModal={() => {
            setShowConfirmationDialog(false);
            onHide && onHide();
          }}
        />
      )}
    </>
  );
};
